import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FaFacebookF, FaInstagram } from "react-icons/fa";

import { 
  getTrendingMovies, 
  getLatestMovies, 
  getPopularSeries, 
  searchMoviesAndSeries,
  getSearchSuggestions 
} from "../api";
import "./Home.css";

// تحويل genre_ids لأسماء الأنواع
const idToGenreName = {
  28: "Action",
  12: "Adventure",
  16: "Animation",
  35: "Comedy",
  80: "Crime",
  99: "Documentary",
  18: "Drama",
  10751: "Family",
  14: "Fantasy",
  36: "History",
  27: "Horror",
  10402: "Music",
  9648: "Mystery",
  10749: "Romance",
  878: "Science Fiction",
  10770: "TV Movie",
  53: "Thriller",
  10752: "War",
  37: "Western"
};

export default function Home() {
  const [featuredMovie, setFeaturedMovie] = useState(null);
  const [trending, setTrending] = useState([]);
  const [latestMovies, setLatestMovies] = useState([]);
  const [latestSeries, setLatestSeries] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  // جلب البيانات الأولية
  useEffect(() => {
    const fetchData = async () => {
      try {
        const trendingMovies = await getTrendingMovies();
        setTrending(trendingMovies.slice(0, 10));
        setFeaturedMovie(trendingMovies[0] || null);

        const latest = await getLatestMovies();
        setLatestMovies(latest.slice(0, 8));

        const series = await getPopularSeries();
        setLatestSeries(series.slice(0, 8));
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };
    fetchData();

    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // جلب اقتراحات البحث
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length > 2) {
        const results = await getSearchSuggestions(searchQuery);
        setSuggestions(results.slice(0, 5));
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };
    const timer = setTimeout(() => {
      if (searchQuery) fetchSuggestions();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearch = async (query = searchQuery) => {
    if (!query.trim()) { 
      setShowResults(false); 
      setSearchResults([]);
      return; 
    }
    setIsSearching(true);
    try {
      const results = await searchMoviesAndSeries(query);
      setSearchResults(results);
      setShowResults(true);
      setShowSuggestions(false);
      localStorage.setItem("lastSearch", query);
    } catch (err) {
      console.error("Search error:", err);
    }
    setIsSearching(false);
  };

  const handleSelectSuggestion = (suggestion) => {
    const title = suggestion.title || suggestion.name || "";
    setSearchQuery(title);
    handleSearch(title);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setShowResults(false);
    setShowSuggestions(false);
    setSearchResults([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
    else if (e.key === "Escape") setShowSuggestions(false);
  };

  const getMediaIcon = (mediaType) => mediaType === "tv" ? "📺" : "🎬";

  if (!featuredMovie) return <div className="loading">Loading...</div>;

  return (
    <div className="home">
      {/* Helmet SEO */}
      <Helmet>
        <title>MovieFree - أفضل موقع لمشاهدة الأفلام والمسلسلات</title>
        <meta name="description" content="MovieFree هو موقع عربي يمكنك من مشاهدة أحدث الأفلام والمسلسلات أونلاين بجودة عالية، مع ترند، أحدث الإصدارات، ونتائج بحث دقيقة وسريعة." />
        <link rel="canonical" href="https://www.moviefree.live/" />

        {featuredMovie && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Movie",
              "name": featuredMovie.title,
              "image": `https://image.tmdb.org/t/p/w500${featuredMovie.poster_path}`,
              "description": featuredMovie.overview,
              "datePublished": featuredMovie.release_date,
              "genre": featuredMovie.genre_ids?.map(id => idToGenreName[id]),
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": featuredMovie.vote_average,
                "ratingCount": featuredMovie.vote_count,
                "bestRating": 10,
                "worstRating": 0
              }
            })}
          </script>
        )}
      </Helmet>

      {/* Search Bar */}
      <div className="search-container" ref={searchRef}>
        <div className="search-bar">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="ابحث عن أفلام، مسلسلات، ممثلين..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => searchQuery.length > 2 && setShowSuggestions(true)}
              className="search-input"
              autoComplete="off"
            />
            {searchQuery && (
              <button className="clear-btn" onClick={handleClearSearch} aria-label="Clear search">×</button>
            )}
            <button 
              className="search-btn"
              onClick={() => handleSearch()}
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? <span className="spinner"></span> : <span className="search-icon">🔍</span>}
            </button>
          </div>

          {/* Suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="suggestions-dropdown" ref={suggestionsRef}>
              {suggestions.map((item) => (
                <div key={item.id} className="suggestion-item" onClick={() => handleSelectSuggestion(item)}>
                  <div className="suggestion-media-type">{getMediaIcon(item.media_type)}</div>
                  <div className="suggestion-content">
                    <div className="suggestion-title">{item.title || item.name}</div>
                    <div className="suggestion-meta">
                      <span className="suggestion-year">{item.release_date?.substring(0, 4) || item.first_air_date?.substring(0, 4) || "N/A"}</span>
                      <span className="suggestion-type">{item.media_type === "tv" ? "مسلسل" : "فيلم"}</span>
                      {item.vote_average !== undefined && item.vote_average !== null && (
                        <span className="suggestion-rating">⭐ {item.vote_average.toFixed(1)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div className="suggestion-footer">
                اضغط <kbd>Enter</kbd> للبحث عن "{searchQuery}"
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Search Results */}
      {showResults && (
        <section className="content-section">
          <div className="section-header">
            <h2>نتائج البحث عن "{searchQuery}"</h2>
            <button className="clear-btn" onClick={handleClearSearch}>مسح البحث</button>
          </div>
          <div className="movie-grid">
            {searchResults.length > 0 ? searchResults.map((item) => (
              <Link
                key={item.id}
                to={item.media_type === "tv" ? `/series/${item.id}` : `/movie/${item.id}`}
                className="movie-card"
              >
                <img
                  src={item.poster_path ? `https://image.tmdb.org/t/p/w500${item.poster_path}` : "/placeholder-poster.jpg"}
                  alt={item.title || item.name}
                  loading="lazy"
                />
                <div className="movie-card-info">
                  <h3>{item.title || item.name}</h3>
                  <p className="rating">{item.vote_average !== undefined && item.vote_average !== null ? `⭐ ${item.vote_average.toFixed(1)}` : "N/A"}</p>
                  <p className="year">{item.release_date?.substring(0,4) || item.first_air_date?.substring(0,4) || "N/A"}</p>
                </div>
              </Link>
            )) : <p>لا توجد نتائج لبحثك.</p>}
          </div>
        </section>
      )}

      {/* Featured Section */}
      {!showResults && featuredMovie && (
        <div className="featured" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(https://image.tmdb.org/t/p/original${featuredMovie.backdrop_path})` }}>
          <div className="overlay">
            <div className="featured-badge">🔥 الأكثر مشاهدة اليوم</div>
            <h1>{featuredMovie.title}</h1>
            <p className="featured-overview">{featuredMovie.overview}</p>
            <div className="featured-meta">
              {featuredMovie.vote_average !== undefined && featuredMovie.vote_average !== null ? (
                <span className="rating">⭐ {featuredMovie.vote_average.toFixed(1)}</span>
              ) : <span className="rating">N/A</span>}
              <span className="year">{featuredMovie.release_date?.substring(0, 4) || "N/A"}</span>
              <span className="language">{featuredMovie.original_language?.toUpperCase() || "N/A"}</span>
            </div>
            <Link to={`/movie/${featuredMovie.id}`} className="watch-btn">
              <span className="play-icon">▶</span> مشاهدة الآن
            </Link>
          </div>
        </div>
      )}

      {/* Trending Section */}
      {!showResults && (
        <section className="content-section">
          <div className="section-header">
            <h2><span className="section-icon">🔥</span> الأكثر مشاهدة الآن</h2>
            <Link to="/trending" className="view-all">عرض الكل →</Link>
          </div>
          <div className="movie-grid">
            {trending.map((movie) => (
              <Link to={`/movie/${movie.id}`} key={movie.id} className="movie-card">
                <div className="movie-card-image">
                  <img src={movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : "/placeholder-poster.jpg"} alt={movie.title} loading="lazy" />
                  <div className="trending-badge">🔥</div>
                </div>
                <div className="movie-card-info">
                  <h3>{movie.title}</h3>
                  <div className="movie-card-meta">
                    {movie.vote_average !== undefined && movie.vote_average !== null ? (
                      <span className="rating">⭐ {movie.vote_average.toFixed(1)}</span>
                    ) : <span className="rating">N/A</span>}
                    <span className="year">{movie.release_date?.substring(0, 4) || "N/A"}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Latest Movies Section */}
      {!showResults && (
        <section className="content-section">
          <div className="section-header">
            <h2><span className="section-icon">🎬</span> أحدث الأفلام</h2>
            <Link to="/movies" className="view-all">عرض الكل →</Link>
          </div>
          <div className="movie-grid">
            {latestMovies.map((movie) => (
              <Link to={`/movie/${movie.id}`} key={movie.id} className="movie-card">
                <img src={movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : "/placeholder-poster.jpg"} alt={movie.title} loading="lazy" />
                <div className="movie-card-info">
                  <h3>{movie.title}</h3>
                  <p className="rating">{movie.vote_average !== undefined && movie.vote_average !== null ? `⭐ ${movie.vote_average.toFixed(1)}` : "N/A"}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Latest Series Section */}
      {!showResults && (
        <section className="content-section">
          <div className="section-header">
            <h2><span className="section-icon">📺</span> أحدث المسلسلات</h2>
            <Link to="/series" className="view-all">عرض الكل →</Link>
          </div>
          <div className="movie-grid">
            {latestSeries.map((series) => (
              <Link to={`/series/${series.id}`} key={series.id} className="movie-card">
                <img src={series.poster_path ? `https://image.tmdb.org/t/p/w500${series.poster_path}` : "/placeholder-poster.jpg"} alt={series.name} loading="lazy" />
                <div className="movie-card-info">
                  <h3>{series.name}</h3>
                  <p className="rating">{series.vote_average !== undefined && series.vote_average !== null ? `⭐ ${series.vote_average.toFixed(1)}` : "N/A"}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-content">
          <p>MovieFree © 2026 - أفضل منصة لمشاهدة الأفلام والمسلسلات أونلاين</p>
          <div className="footer-social-icons">
            <a href="https://www.facebook.com/profile.php?id=100063468409303" target="_blank" rel="noopener noreferrer">
              <FaFacebookF size={20} />
            </a>
            <a href="https://www.instagram.com/YourPage" target="_blank" rel="noopener noreferrer">
              <FaInstagram size={20} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
